import { z } from "zod";
const normalizedIndianMobile = (value) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length === 12 && digits.startsWith("91")) {
        return digits.slice(2);
    }
    return digits;
};
export const phoneSchema = z.string()
    .trim()
    .min(10)
    .max(15)
    .refine((value) => /^[6-9]\d{9}$/.test(normalizedIndianMobile(value)), {
    message: "Enter a valid 10-digit Indian mobile number",
});
export const idempotencyKeySchema = z.string().uuid();
const optionalTrimmed = (max) => z.string().trim().max(max).optional().or(z.literal(""));
const emptyToUndefined = (value) => (value === "" ? undefined : value);
const optionalEnvString = z.preprocess(emptyToUndefined, z.string().trim().min(1).optional());
const booleanEnv = z.preprocess((value) => {
    if (value === undefined || value === "")
        return undefined;
    if (typeof value === "boolean")
        return value;
    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["true", "1", "yes", "on"].includes(normalized))
            return true;
        if (["false", "0", "no", "off"].includes(normalized))
            return false;
    }
    return value;
}, z.boolean());
const productionPlaceholderPattern = /placeholder|change_before|local_dev|do_not_send|cream_dev_password/i;
const assertProductionSecret = (ctx, value, path, label) => {
    if (!value || productionPlaceholderPattern.test(value)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path,
            message: `${label} must be configured with a real non-placeholder value`,
        });
    }
};
export const customerIdentityUpdateSchema = z.object({
    name: z.string().trim().min(2).max(160).optional(),
    email: z.string().trim().email().max(180).optional().or(z.literal("")),
    phone: phoneSchema.optional().or(z.literal(""))
}).refine((input) => input.name !== undefined || input.email !== undefined || input.phone !== undefined, {
    message: "At least one identity field is required"
});
export const customerAddressUpsertSchema = z.object({
    label: z.string().trim().min(2).max(60),
    recipientName: z.string().trim().min(2).max(120),
    phone: phoneSchema,
    addressLine1: z.string().trim().min(5).max(180),
    addressLine2: optionalTrimmed(180),
    landmark: optionalTrimmed(120),
    city: z.string().trim().min(2).max(80),
    state: z.string().trim().min(2).max(80),
    postalCode: z.string().trim().min(4).max(12),
    deliveryNotes: optionalTrimmed(300),
    isPrimary: z.boolean().optional(),
    isVerified: z.boolean().optional()
});
export const customerOccasionTypeSchema = z.enum(["BIRTHDAY", "ANNIVERSARY", "BUSINESS_EVENT", "FESTIVAL", "CUSTOM"]);
export const customerOccasionSchema = z.object({
    type: customerOccasionTypeSchema,
    label: z.string().trim().min(2).max(100),
    personName: optionalTrimmed(100),
    month: z.number().int().min(1).max(12),
    day: z.number().int().min(1).max(31),
    year: z.number().int().min(1900).max(2200).optional(),
    recurrence: z.enum(["YEARLY", "ONCE"]).default("YEARLY"),
    relationship: optionalTrimmed(80),
    isVerified: z.boolean().optional(),
    notes: optionalTrimmed(300)
}).superRefine((input, ctx) => {
    const year = input.year ?? 2024;
    const date = new Date(Date.UTC(year, input.month - 1, input.day));
    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== input.month - 1 || date.getUTCDate() !== input.day) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["day"], message: "Invalid occasion date" });
    }
});
export const customerPreferenceCategorySchema = z.enum([
    "FLAVOR",
    "CAKE_TYPE",
    "EGG_PREFERENCE",
    "DESIGN_STYLE",
    "COLOR",
    "THEME",
    "BUDGET_RANGE",
    "WEIGHT_RANGE",
    "DELIVERY_TIME",
    "PACKAGING",
    "COMMUNICATION_CHANNEL",
    "DIETARY",
    "ALLERGY",
    "DISLIKE",
    "OTHER"
]);
export const customerPreferenceSchema = z.object({
    category: customerPreferenceCategorySchema,
    value: z.string().trim().min(2).max(120),
    sentiment: z.enum(["POSITIVE", "NEGATIVE", "NEUTRAL"]).default("POSITIVE"),
    isVerified: z.boolean().optional(),
    notes: optionalTrimmed(300)
});
export const customerNoteSchema = z.object({
    body: z.string().trim().min(2).max(1000),
    category: z.enum(["GENERAL", "SALES", "SERVICE", "OWNER_PRIVATE"]).default("GENERAL"),
    visibility: z.enum(["CUSTOMER_TEAM", "MANAGERS_ONLY", "OWNER_ADMIN_ONLY"]).default("CUSTOMER_TEAM"),
    isPinned: z.boolean().optional()
});
export const customerRelationshipAssignmentSchema = z.object({
    assignedUserId: z.string().uuid(),
    reason: optionalTrimmed(240)
});
export const inquiryStatusSchema = z.enum([
    "NEW",
    "CONTACTED",
    "REQUIREMENT_TAKEN",
    "QUOTED",
    "FOLLOWUP_DUE",
    "NEGOTIATION",
    "ADVANCE_PENDING",
    "CONVERTED",
    "LOST",
    "DORMANT",
    "REACTIVATION_ALLOWED",
    "REOPENED"
]);
export const lostReasonSchema = z.string().trim().min(2).max(120);
export const leadStatusTransitionSchema = z.object({
    status: inquiryStatusSchema,
    lostReason: lostReasonSchema.optional(),
    lostReasonNotes: z.string().trim().max(1000).optional()
});
export const leadConversationUpdateSchema = z.object({
    summary: z.string().trim().min(3).max(2000),
    channel: z.string().trim().min(2).max(40).default("WHATSAPP"),
    customerSentiment: z.string().trim().max(120).optional(),
    nextAction: z.string().trim().max(300).optional(),
    nextFollowUpAt: z.string().datetime().optional().or(z.literal("")),
    saleHappened: z.boolean().optional(),
    status: inquiryStatusSchema.optional().or(z.literal("")),
    lostReason: lostReasonSchema.optional().or(z.literal("")),
    lostReasonNotes: z.string().trim().max(1000).optional()
});
export const createOrderSchema = z.object({
    customerName: z.string().min(1).max(200),
    customerPhone: phoneSchema,
    customerEmail: z.string().email().optional().or(z.literal("")),
    occasion: z.string().min(1),
    source: z.string().min(1),
    deliveryDate: z.string().min(1),
    deliveryTime: z.string().min(1),
    deliveryType: z.enum(["HOME_DELIVERY", "SELF_PICKUP"]),
    deliveryAddress: z.string().optional(),
    recipientName: z.string().optional(),
    recipientPhone: phoneSchema.optional(),
    cakeWeightLbs: z.number().positive(),
    flavor: z.string().min(1),
    layers: z.number().int().positive().default(1),
    finishType: z.string().min(1),
    messageOnCake: z.string().optional(),
    specialInstructions: z.string().optional(),
    basePricePaise: z.number().int().nonnegative(),
    discountValue: z.number().nonnegative().default(0),
    advancePaidPaise: z.number().int().nonnegative(),
    paymentMode: z.enum(["CASH", "UPI"]),
    paymentReference: z.string().optional()
});
export const paymentConfirmationSchema = z.object({
    orderId: z.string().uuid(),
    paymentMode: z.enum(["CASH", "UPI"]),
    paymentReference: z.string().optional(),
    amountPaise: z.number().int().nonnegative()
});
export const envSchema = z.object({
    NODE_ENV: z.enum(["development", "test", "staging", "production"]).default("development"),
    PORT: z.coerce.number().int().positive().default(4000),
    DATABASE_URL: z.string().trim().min(1),
    JWT_SECRET: z.string().trim().min(32),
    JWT_REFRESH_SECRET: z.string().trim().min(32),
    FRONTEND_URL: z.string().trim().url().min(1),
    BACKEND_URL: optionalEnvString,
    CORS_ORIGINS: z.string().trim().default(""),
    TRUST_PROXY: booleanEnv.default(false),
    APP_TIMEZONE: z.string().trim().default("Asia/Kolkata"),
    LOG_LEVEL: z.enum(["debug", "info", "warn", "error"]).default("info"),
    STORAGE_PROVIDER: z.enum(["r2", "s3"]).default("r2"),
    STORAGE_BUCKET: z.string().trim().min(1).default("cream-creations"),
    STORAGE_ACCESS_KEY: z.string().trim().min(1),
    STORAGE_SECRET_KEY: z.string().trim().min(1),
    STORAGE_REGION: z.string().trim().min(1).default("auto"),
    STORAGE_ENDPOINT: optionalEnvString,
    OPENROUTESERVICE_API_KEY: optionalEnvString,
    SMTP_ENABLED: booleanEnv.default(false),
    SMTP_HOST: optionalEnvString,
    SMTP_PORT: z.coerce.number().int().positive().default(587),
    SMTP_USER: optionalEnvString,
    SMTP_PASS: optionalEnvString,
    WHATSAPP_OUTBOUND_ENABLED: booleanEnv.default(false),
    WHATSAPP_API_KEY: optionalEnvString,
    WHATSAPP_PHONE_NUMBER_ID: optionalEnvString,
    WHATSAPP_VERIFY_TOKEN: optionalEnvString,
    WHATSAPP_APP_SECRET: optionalEnvString,
    WHATSAPP_SIGNATURE_VERIFICATION_ENABLED: booleanEnv.default(false),
    META_APP_ID: optionalEnvString,
    WHATSAPP_EMBEDDED_SIGNUP_CONFIG_ID: optionalEnvString,
    WHATSAPP_GRAPH_VERSION: z.string().trim().regex(/^v\d+\.\d+$/).default("v23.0"),
    WHATSAPP_TOKEN_ENCRYPTION_KEY: z.preprocess(emptyToUndefined, z.string().trim().min(32).optional()),
    GOOGLE_SHEETS_SYNC_ENABLED: booleanEnv.default(false),
    GOOGLE_APPS_SCRIPT_URL: optionalEnvString,
    GOOGLE_SHEETS_SHARED_SECRET: z.preprocess(emptyToUndefined, z.string().trim().min(32).optional()),
    SENTRY_DSN_BACKEND: optionalEnvString,
    SENTRY_DSN_FRONTEND: optionalEnvString,
    RATE_LIMIT_WINDOW_MS: z.coerce.number().int().positive().default(900000),
    RATE_LIMIT_MAX: z.coerce.number().int().positive().default(1000),
    AUTH_RATE_LIMIT_MAX: z.coerce.number().int().positive().default(10),
    DIGEST_HOUR_IST: z.coerce.number().int().min(0).max(23).default(8),
    DIGEST_MINUTE_IST: z.coerce.number().int().min(0).max(59).default(0),
    ENABLE_DEMO_SEED: booleanEnv.default(false),
    BACKUP_ENABLED: booleanEnv.default(false),
    BACKUP_DIR: optionalEnvString,
    BACKUP_RETENTION_DAYS: z.coerce.number().int().positive().default(14),
}).superRefine((input, ctx) => {
    const productionLike = input.NODE_ENV === "staging" || input.NODE_ENV === "production";
    const corsOrigins = input.CORS_ORIGINS ? input.CORS_ORIGINS.split(",").map((origin) => origin.trim()).filter(Boolean) : [];
    for (const [index, origin] of corsOrigins.entries()) {
        const parsed = z.string().url().safeParse(origin);
        if (!parsed.success) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["CORS_ORIGINS", index],
                message: "CORS_ORIGINS must contain comma-separated URL origins",
            });
        }
    }
    if (input.SMTP_ENABLED) {
        assertProductionSecret(ctx, input.SMTP_HOST, ["SMTP_HOST"], "SMTP_HOST");
        assertProductionSecret(ctx, input.SMTP_USER, ["SMTP_USER"], "SMTP_USER");
        assertProductionSecret(ctx, input.SMTP_PASS, ["SMTP_PASS"], "SMTP_PASS");
    }
    if (input.WHATSAPP_OUTBOUND_ENABLED) {
        assertProductionSecret(ctx, input.WHATSAPP_API_KEY, ["WHATSAPP_API_KEY"], "WHATSAPP_API_KEY");
        assertProductionSecret(ctx, input.WHATSAPP_PHONE_NUMBER_ID, ["WHATSAPP_PHONE_NUMBER_ID"], "WHATSAPP_PHONE_NUMBER_ID");
    }
    if (input.WHATSAPP_SIGNATURE_VERIFICATION_ENABLED) {
        assertProductionSecret(ctx, input.WHATSAPP_VERIFY_TOKEN, ["WHATSAPP_VERIFY_TOKEN"], "WHATSAPP_VERIFY_TOKEN");
        assertProductionSecret(ctx, input.WHATSAPP_APP_SECRET, ["WHATSAPP_APP_SECRET"], "WHATSAPP_APP_SECRET");
    }
    if (input.GOOGLE_SHEETS_SYNC_ENABLED) {
        assertProductionSecret(ctx, input.GOOGLE_APPS_SCRIPT_URL, ["GOOGLE_APPS_SCRIPT_URL"], "GOOGLE_APPS_SCRIPT_URL");
        assertProductionSecret(ctx, input.GOOGLE_SHEETS_SHARED_SECRET, ["GOOGLE_SHEETS_SHARED_SECRET"], "GOOGLE_SHEETS_SHARED_SECRET");
    }
    if (!productionLike)
        return;
    assertProductionSecret(ctx, input.DATABASE_URL, ["DATABASE_URL"], "DATABASE_URL");
    assertProductionSecret(ctx, input.JWT_SECRET, ["JWT_SECRET"], "JWT_SECRET");
    assertProductionSecret(ctx, input.JWT_REFRESH_SECRET, ["JWT_REFRESH_SECRET"], "JWT_REFRESH_SECRET");
    assertProductionSecret(ctx, input.STORAGE_ACCESS_KEY, ["STORAGE_ACCESS_KEY"], "STORAGE_ACCESS_KEY");
    assertProductionSecret(ctx, input.STORAGE_SECRET_KEY, ["STORAGE_SECRET_KEY"], "STORAGE_SECRET_KEY");
    assertProductionSecret(ctx, input.STORAGE_BUCKET, ["STORAGE_BUCKET"], "STORAGE_BUCKET");
    if (input.ENABLE_DEMO_SEED) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["ENABLE_DEMO_SEED"],
            message: "ENABLE_DEMO_SEED must be false outside local development",
        });
    }
});
